import React from 'react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import '../App.css'
import Modal from '@mui/material/Modal'
import { useAuthInfo } from '@propelauth/react'

import { 
    Box, 
    CircularProgress, 
    TextField, 
    Button, 
    Typography, 
    IconButton,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions 
} from '@mui/material'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
    createNewDocumentSet,
    getDocumentSets,
    newDocumentSetName,
    deleteDocumentSet,
    updateDocumentSetName
} from '../utils/utils'
import { useDemo } from '../contexts/DemoContext'
import { useUser } from '../contexts/UserContext'
import CustomTable, { CustomTableSkeleton } from '../components/CustomTable'
import OptionsMenu from '../components/OptionsMenu'
import { formatRelativeDate, getDateFromObjectId, formatCreatedDate, formatDateYYYYMMDD } from '../utils/dateUtils'
import RenameFileModal from './TemplatesPages/modals/RenameFileModal'

function HomePage() {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [documentSets, setDocumentSets] = useState([])
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const [newDocumentSetName, setNewDocumentSetName] = useState('')

    const [newDocumentModal, setNewDocumentModal] = useState(false)
    const [isCreating, setIsCreating] = useState(false)
    
    // Menu states
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedDocumentSet, setSelectedDocumentSet] = useState(null)
    const [documentSetToDelete, setDocumentSetToDelete] = useState(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedDocumentSetToRename, setSelectedDocumentSetToRename] = useState(null)
    const [isRenameFileModalOpen, setIsRenameFileModalOpen] = useState(false)
    const [originalName, setOriginalName] = useState('')

    const handleNewDocumentModalOpen = () => setNewDocumentModal(true)
    const handleNewDocumentModalClose = () => setNewDocumentModal(false)
    const { isDemo, setIsDemo } = useDemo()
    const { roles } = useUser()

    // Check if user is a Member (read-only access)
    const isMemberRole = roles.includes('Member')

    const navigate = useNavigate()

    const handleRowClick = (row) => {
        navigate(`/documentSet/${row.document_set_name}`, {
            state: { ...row },
        })
    }

    // Menu handlers
    const handleMenuClick = (event, documentSet) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
        setSelectedDocumentSet(documentSet)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        // setSelectedDocumentSet(null)
    }

    const handleDeleteClick = () => {
        setDocumentSetToDelete(selectedDocumentSet)
        setDeleteDialogOpen(true)
        setSelectedDocumentSet(null)
        handleMenuClose()
    }

    // const handleDuplicate = () => {
    //     console.log('Duplicate document set:', selectedDocumentSet?.document_set_name)
    //     // Add duplicate logic here
    // }

    // const handleShare = () => {
    //     console.log('Share document set:', selectedDocumentSet?.document_set_name)
    //     // Add share logic here
    // }

    // const handleSeeHistory = () => {
    //     console.log('See history for document set:', selectedDocumentSet?.document_set_name)
    //     // Add history logic here
    // }

    const updateSelectedDocumentToRename = (selected) => {
        const updatedSelected = { ...selectedDocumentSetToRename, ...selected }
        setSelectedDocumentSetToRename(updatedSelected)
    }

    const clearSelectedDocumentToRename = () => {
        setSelectedDocumentSetToRename(null)
        setSelectedDocumentSet(null)
    }

    const handleSaveChanges = async () => {
        console.log('=== RENAME DOCUMENT SET: Save button clicked ===')
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        console.log('selectedDocumentSetToRename', selectedDocumentSetToRename)
        const jsonPayload = {
            document_set_id: selectedDocumentSetToRename.document_set_id,
            old_name: originalName,
            new_name: selectedDocumentSetToRename.document_set_name
        }
        console.log('JSON Payload being sent to backend:', jsonPayload)
        const result = await updateDocumentSetName(orgAccessToken, jsonPayload)
        console.log('Rename result:', result)

        // Refresh document sets and update cache
        try {
            const refreshedResult = await getDocumentSets(
                setLoading,
                setError,
                orgAccessToken
            )
            if (refreshedResult && refreshedResult.length > 0) {
                const transformedData = refreshedResult.map((item) => ({
                    docs_generated: item.documents?.length || 0,
                    created: formatDateYYYYMMDD(item),
                    ...item,
                }))
                setDocumentSets(transformedData)
            }
        } catch (error) {
            console.error('Error refreshing document sets:', error)
        }
    }

    const hasChangesDocumentToRename = () => {
        return JSON.stringify(selectedDocumentSetToRename) !== JSON.stringify(selectedDocumentSet)
    }

    const handleRenameClick = () => {
        setOriginalName(selectedDocumentSet.document_set_name)
        setSelectedDocumentSetToRename(selectedDocumentSet)
        setIsRenameFileModalOpen(true)
        handleMenuClose()
    }

    const handleRenameFileModalClose = () => {
        setIsRenameFileModalOpen(false)
    }

    const handleDeleteConfirm = async () => {
        // Placeholder delete function - just log/show message
        console.log('Delete document:', documentSetToDelete.document_set_name)
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        const jsonPayload = {       
            document_id: documentSetToDelete.id
        }
        await deleteDocumentSet(orgAccessToken, jsonPayload)
        
        
        navigate(`/`)
        // You could show a snackbar or alert here if needed
        setDeleteDialogOpen(false)
        setDocumentSetToDelete(null)
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false)
        setDocumentSetToDelete(null)
        setSelectedDocumentSet(null)
    }

    const authInfo = useAuthInfo()

    const orgs = authInfo.orgHelper.getOrgs()
    console.log('orgs', orgs)

    async function getOrgAccessToken(orgId) {
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
        console.log('orgAccessToken', orgAccessToken)
        return orgAccessToken
    }

    useEffect(() => {
        async function retrieveData() {
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            setLoading(true) // Start loading
            try {
                const result = await getDocumentSets(
                    setLoading,
                    setError,
                    orgAccessToken
                ) // Assuming getDocumentSets returns the data
                console.log('result', result)
                if (result && result.length > 0) {
                    const transformedData = result.map((item) => ({
                        docs_generated: item.documents?.length || 0, // Length of the documents array
                        created: formatDateYYYYMMDD(item),
                        ...item,
                    }))

                    console.log('transformedData', transformedData)

                    setDocumentSets(transformedData)

                    // setDocumentSets(result) // Update state with fetched data
                    setError(false) // Reset error state if successful
                } else {
                    console.log('No documents found')
                    setDocumentSets([]) // Set document sets to an empty array
                    setError(false) // No error, just no documents found
                }
            } catch (error) {
                console.error(error)
                setLoading(false) // End loading regardless of outcome
                // setError(true); // Set error state
            } finally {
                setLoading(false) // End loading regardless of outcome
            }
        }
        retrieveData()
    }, [])

    // Transform data for new table structure
    const transformedData = documentSets.map((documentSet) => ({
        ...documentSet,
        folder: documentSet.document_set_name,
        owner: documentSet.user,
        documents: documentSet.docs_generated,
        created: formatCreatedDate(documentSet)
    }))

    // Calculate pagination
    // const startIndex = (currentPage - 1) * rowsPerPage
    // const endIndex = startIndex + rowsPerPage
    // const paginatedData = transformedData.slice(startIndex, endIndex)
    // const totalPages = Math.ceil(transformedData.length / rowsPerPage)

    // console.log('paginatedData', paginatedData)

    // Define table columns for new UI
    const tableColumns = [
        {
            field: 'folder',
            header: 'Workspace',
            width: '80%',
        },
        // {
        //     field: 'owner',
        //     header: 'Owner',
        //     width: '30%',
        // },
        {
            field: 'documents',
            header: 'Documents',
            width: '20%',
        },
    ]

    return (
        <Box sx={{ // Full width container
            width: '100%',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            height: '100%',
            overflowY: 'auto',
        }}>
            <Box sx={{ // Inner container
                width: '100%', 
                height: '100%', 
                padding: '40px 64px', 
                boxSizing: 'border-box',
                gap: '24px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F9FAFB',
            }}>
                <Box sx={{ // Header container
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}>
                    {/* {isDemo && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Organize various documents by drug development or
                            document type in a "Document Set".
                        </Alert>
                    )} */}
                    <Box 
                        sx={{
                            width: '100%',
                            margin: '0 auto',
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between', 
                        }}
                    >
                        <Box sx={{ 
                            gap: '8px',
                            display: 'flex', 
                            flexDirection: 'column' 
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <Typography sx={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    lineHeight: '36px',
                                    color: '#374151',
                                }}>
                                    Workspaces
                                </Typography>
                                <IconButton
                                    onClick={() => {}}
                                    disabled={false}
                                    title=""
                                    size="small"
                                    sx={{
                                        color: '#6B7280',
                                        '&:hover': {
                                            backgroundColor: '#F3F4F6',
                                        },
                                    }}
                                >
                                    {/* <RefreshIcon /> */}
                                    
                                </IconButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#374151',
                                }}>
                                    
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{
                            gap: '8px',
                            display: 'flex',
                            paddingBottom: '8px',
                            alignItems: 'flex-start',
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => !isMemberRole && handleNewDocumentModalOpen()}
                                disabled={isMemberRole}
                                sx={{
                                    backgroundColor: '#1F477D',
                                    // borderColor: '#FFFFFF',
                                    color: '#FFFFFF',
                                    height: '34px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#9CA3AF',
                                        color: '#E5E7EB',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            >
                                Create Workspace
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {loading ? (
                    <CustomTableSkeleton
                        columns={tableColumns}
                        backgroundColor={'#F9FAFB'}
                    />
                ) : (
                    <Box sx={{
                        height: '100%'
                    }}>
                        <CustomTable
                            columns={tableColumns}
                            data={transformedData}
                            onRowClick={handleRowClick}
                            onMenuClick={handleMenuClick}
                            backgroundColor={'#F9FAFB'}
                        />
                    </Box>
                )}
                
                {/* Options Menu */}
                <OptionsMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    // onDelete={handleDeleteClick} // Removed because no document set delete function
                    onRename={handleRenameClick}
                />
            </Box>
            <Modal
                open={newDocumentModal}
                onClose={handleNewDocumentModalClose}
            >
                <Box className="modal-background" sx={{
                    width: '400px',
                }}>
                    <Box className="modal-content" sx={{
                        width: '100%',
                    }}>
                        <Box className="form-container" sx={{
                            width: '100%',
                            height: '100%',
                        }}>
                            <Box sx={{
                                width: '100%',
                                mb: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                            }}>
                                <Typography sx={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    lineHeight: '36px',
                                    color: '#374151',
                                }}>
                                    Create a Workspace
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: '100%',
                                mb: '24px',
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#6B7280',
                                }}>
                                    Enter a name for this Workspace
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: '100%',
                                mb: '16px',
                            }}>
                            <TextField
                                sx={{
                                    width: '100%',
                                    mb: '8px',
                                }}
                                size="small"
                                variant="outlined"
                                value={newDocumentSetName}
                                placeholder="Workspace name"
                                onChange={(e) =>
                                    setNewDocumentSetName(e.target.value)
                                }
                            />
                            </Box>
                            <Button
                                sx={{
                                    backgroundColor: '#1F477D',
                                    color: '#FFFFFF',
                                    height: '34px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#9CA3AF',
                                        color: '#FFFFFF',
                                    },
                                }}
                                disabled={isCreating || newDocumentSetName.length === 0}
                                onClick={async () => {
                                    setIsCreating(true)
                                    try {
                                        const orgAccessToken =
                                            await getOrgAccessToken(
                                                orgs[0].orgId
                                            )
                                        await createNewDocumentSet(
                                            newDocumentSetName,
                                            orgAccessToken,
                                            orgAccessToken
                                        )
                                        handleNewDocumentModalClose()
                                        navigate(
                                            `/documentSet/${newDocumentSetName}`
                                        )
                                    } catch (error) {
                                        console.error(
                                            'Error creating document set:',
                                            error
                                        )
                                        setError(true)
                                    } finally {
                                        setIsCreating(false)
                                    }
                                }}
                            >
                                {isCreating ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{ color: 'white' }}
                                    />
                                ) : (
                                    <p className="text-white">Continue</p>
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Delete Document Set</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{documentSetToDelete?.document_set_name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Modal 
                open={error} 
                onClose={() => setError(false)}
            >
                <Box
                    className="modal-background"
                    style={{ width: '40%', height: '40%' }}
                >
                    <Box className="modal-content">
                        Error. Please try again.
                    </Box>
                </Box>
            </Modal>
            <RenameFileModal
                open={isRenameFileModalOpen}
                onClose={handleRenameFileModalClose}
                modalTitle="Document Set"
                selected={selectedDocumentSetToRename}
                value="document_set_name"
                updateSelected={updateSelectedDocumentToRename}
                saveChanges={handleSaveChanges}
                clearSelected={clearSelectedDocumentToRename}
                hasChanges={hasChangesDocumentToRename}
            />
        </Box>
    )
}

export default HomePage
